import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { debounceTime, Observable, takeUntil, tap } from 'rxjs';
import { PaymentMethod } from '../../../../../../core/enums/commerce/payment/payment-method';
import { CreditCardSummary } from '../../../../../../core/models/commerce/cards-on-file/credit-card-summary';
import { OrderCreditCardSummary } from '../../../../../../core/models/commerce/cards-on-file/order-credit-card-summary';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { PaymentMethodValidatorMap } from '../../constants/payment-method-validator-map';
import { PaymentSourceFormModel } from '../../models/payment-source-form-model';

/**
 * Abstract service class for managing payment source forms. This class provides the structure and common functionality for handling payment source forms,
 * including defining the form structure, managing validators based on the selected payment method, and setting up listeners for form control changes.
 */
export abstract class PaymentSourceFormService<
    TModel extends PaymentSourceFormModel,
> {
    /**
     * Provides a mapping of payment methods to their corresponding form control validators.
     * This method must be implemented by subclasses to return the appropriate validators for each payment method,
     * based on the current list of credit card summaries (used for card-on-file payment methods).
     */
    protected abstract getPaymentMethodValidatorMap(
        getCreditCards: () => CreditCardSummary[],
    ): PaymentMethodValidatorMap<TModel>;

    /**
     * Refreshes the validators on the form controls based on the currently selected payment method
     * and applies them to the form controls.
     *
     * @param form The form group containing the payment method controls to which the validators should be applied.
     * @param getCreditCards A function that returns the current list of credit card summaries, used to determine
     * the appropriate validators for card-on-file payment methods.
     */
    refreshAndApplyPaymentMethodValidators(
        form: FormGroup<FormGroupType<TModel>>,
        getCreditCards: () => CreditCardSummary[],
    ): void {
        const paymentMethod = form.controls.paymentMethod
            .value as PaymentMethod | null;

        if (!paymentMethod) {
            return;
        }

        // Get the validator map for the current credit cards and retrieve the validators for the selected payment method
        const validatorMap = this.getPaymentMethodValidatorMap(getCreditCards);
        const validators = validatorMap[paymentMethod];

        if (validators) {
            // Iterate over the controls that require validators for the selected payment method and apply them
            const controlValidators = Object.entries(validators) as [
                Extract<keyof TModel, string>,
                ValidatorFn[],
            ][];
            controlValidators.forEach(([controlName, controlValidators]) => {
                const control = form.get(controlName);
                if (!control) {
                    return;
                }

                // Clear existing validators and set the new validators for the control, then update its validity
                control.clearValidators();
                control.setValidators(controlValidators);
                control.updateValueAndValidity({ emitEvent: false });
            });
        }
    }

    /**
     * Sets up listeners on the form controls to automatically refresh and apply validators when the payment method changes.
     *
     * @param form The form group containing the payment method controls to which the listeners should be applied.
     * @param getCreditCards A function that returns the current list of credit card summaries, used to determine
     * the appropriate validators for card-on-file payment methods.
     * @param destroy$ An observable that emits when the component is destroyed, used to unsubscribe from value changes.
     */
    setupFormListeners(
        form: FormGroup<FormGroupType<TModel>>,
        getCreditCards: () => CreditCardSummary[],
        destroy$: Observable<void>,
    ): void {
        const paymentMethodControl = form.controls
            .paymentMethod as FormControl<PaymentMethod | null>;

        paymentMethodControl.valueChanges
            .pipe(
                takeUntil(destroy$),
                debounceTime(0), // Ensures that the form control values are updated before we read them
                tap(() =>
                    this.refreshAndApplyPaymentMethodValidators(
                        form,
                        getCreditCards,
                    ),
                ),
            )
            .subscribe();
    }

    /**
     * Selects the preferred or default card on file for the user and patches the form with the selected card ID.
     * The selection logic prioritizes a pre-selected card ID (if provided), then the user's preferred card, and
     * finally the default card if neither of the previous options are available.
     *
     * @param form The form group containing the cardOnFileId control to which the selected card ID should be applied.
     * @param getCreditCards A function that returns the current list of credit card summaries from which to select the preferred or default card.
     * @param preSelectedCardOnFileId Optional ID of the card on file to pre-select in the form. If not provided, the method will apply the preferred/default card selection logic.
     */
    selectPreferredOrDefaultCard(
        form: FormGroup<FormGroupType<TModel>>,
        getCreditCards: () => OrderCreditCardSummary[],
        preSelectedCardOnFileId: number | null = null,
    ): void {
        const preferredCardId = getCreditCards().find(
            (card) => card.isPreferred,
        )?.id;
        const defaultCardId = getCreditCards().find(
            (card) => card.isDefault,
        )?.id;

        const selectedCardId =
            preSelectedCardOnFileId || preferredCardId || defaultCardId;

        if (selectedCardId) {
            form.controls.cardOnFileId.patchValue(selectedCardId, {
                emitEvent: false,
            });
        }
    }

    /**
     * Selects the default card on file for the user and patches the form with the selected card ID.
     * The method looks for the card marked as default in the list of credit cards.
     * @param form The form group containing the cardOnFileId control to which the selected card ID should be applied.
     * @param getCreditCards A function that returns the current list of credit card summaries from which to select the default card.
     */
    selectDefaultCard(
        form: FormGroup<FormGroupType<TModel>>,
        getCreditCards: () => CreditCardSummary[],
    ): void {
        const defaultCard = getCreditCards().find((card) => card.isDefault);

        if (defaultCard) {
            form.controls.cardOnFileId.patchValue(defaultCard.id, {
                emitEvent: false,
            });
        }
    }
}
